// UnifiedPoolScenarioBar.tsx - Compact toolbar with dropdown pool switcher and pill scenario buttons
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WinnerWithMetadata, WinnerSource } from '../types/types';
import PoolEditModal from './PoolEditModal';
import AddPoolModal from './AddPoolModal';
import {
  getUserPools,
  savePoolWithConfig,
  deletePool,
  getUserBrackets,
  saveBracket,
  deleteScenario,
  getKnownWinners,
  createShare,
} from '../api/api';
import { getAllPlayerNames } from '../utils/playerPicksUtils';
import { validateAndCleanKnownWinners } from '../utils/bracketValidator';
import { useConfig } from '../context/ConfigContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Loader2, X, FolderOpen, ChevronDown, Save, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Share2, Copy, Check } from 'lucide-react';
import { cn } from '../lib/utils';

interface Pool {
  name: string;
  playerPicks: string;
  created_at: string;
  pointsConfig?: {
    pointValues: number[];
  };
  pinnedPlayers?: string[];
}

interface SavedScenario {
  name: string;
  knownWinners: string[];
  created_at: string;
}

interface UnifiedPoolScenarioBarProps {
  // Pool-related props
  currentPlayerPicks: string;
  onPlayerPicksChange: (picks: string) => void;
  onPlayersExtracted: (players: string[]) => void;

  // Scenario-related props
  winnersWithMetadata: WinnerWithMetadata[];
  onWinnersUpdate: (winners: WinnerWithMetadata[], error?: string) => void;
  onValidationError: (error: string | null) => void;
  hasUserMadeChanges: boolean;

  // Bracket control props
  onFillWithHigherSeeds: () => void;
  onFillWithLowerSeeds: () => void;
  onClearBracket: () => void;

  // Help callback
  onFirstPoolImport?: () => void;

  // Notify parent when current pool changes (name + pinned players)
  onPoolChange?: (poolName: string | null, pinnedPlayers: string[]) => void;

  // Notify parent when pools finish loading (with names)
  onPoolsLoaded?: (poolNames: string[]) => void;

  // External import trigger (from onboarding wizard)
  externalImportPoolName?: string | null;

  // Portal target for pool selector (renders pool UI into this element when provided)
  poolSelectorPortal?: HTMLElement | null;

  // Portal target for scenario selector dropdown (renders into bracket heading)
  scenarioSelectorPortal?: HTMLElement | null;

  // External trigger to open save scenario dialog (increment to open)
  saveDialogTrigger?: number;

  // External trigger to open share scenario dialog (increment to open)
  shareDialogTrigger?: number;

  // External trigger to open add pool modal (increment to open)
  advancedPoolTrigger?: number;
}

// Default point values for 6 rounds
const DEFAULT_POINT_VALUES = [1, 2, 4, 8, 16, 32];

const UnifiedPoolScenarioBar: React.FC<UnifiedPoolScenarioBarProps> = ({
  currentPlayerPicks,
  onPlayerPicksChange,
  onPlayersExtracted,
  winnersWithMetadata,
  onWinnersUpdate,
  onValidationError,
  hasUserMadeChanges,
  onFillWithHigherSeeds,
  onFillWithLowerSeeds,
  onClearBracket,
  onFirstPoolImport,
  onPoolChange,
  onPoolsLoaded,
  externalImportPoolName,
  poolSelectorPortal,
  scenarioSelectorPortal,
  saveDialogTrigger,
  shareDialogTrigger,
  advancedPoolTrigger,
}) => {
  // Pool state
  const [pools, setPools] = useState<Pool[]>([]);
  const [currentPool, setCurrentPool] = useState<Pool | null>(null);
  const [isPoolEditModalOpen, setIsPoolEditModalOpen] = useState(false);
  const [isCreatePoolModalOpen, setIsCreatePoolModalOpen] = useState(false);
  const [isAddPoolModalOpen, setIsAddPoolModalOpen] = useState(false);

  // Notify parent when current pool changes and persist selection
  useEffect(() => {
    onPoolChange?.(currentPool?.name || null, currentPool?.pinnedPlayers || []);
    if (currentPool?.name) {
      localStorage.setItem('lastSelectedPool', currentPool.name);
    }
  }, [currentPool?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scenario state
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>([]);
  const [activeScenarioName, setActiveScenarioName] = useState<string>('current');

  // Save scenario dialog state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [saveError, setSaveError] = useState('');

  // Delete pool dialog state
  const [showDeletePoolDialog, setShowDeletePoolDialog] = useState(false);
  const [poolToDelete, setPoolToDelete] = useState<string | null>(null);

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Official winners state
  const [officialWinners, setOfficialWinners] = useState<string[]>([]);

  // Loading and error state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Get configuration context
  const { applyPoolScoring } = useConfig();

  // Helper function to check if pool uses custom scoring
  const isCustomScoring = (pool: Pool): boolean => {
    if (!pool.pointsConfig) return false;

    const { pointValues } = pool.pointsConfig;

    // Compare with default values
    if (pointValues.length !== DEFAULT_POINT_VALUES.length) return true;

    for (let i = 0; i < pointValues.length; i++) {
      if (pointValues[i] !== DEFAULT_POINT_VALUES[i]) return true;
    }

    return false;
  };

  // Apply pool configuration to context (only scoring now)
  const applyPoolConfiguration = (pool: Pool) => {
    const pointValues = pool.pointsConfig?.pointValues || DEFAULT_POINT_VALUES;

    // Apply scoring to context
    applyPoolScoring(pointValues);
  };

  // Reset active scenario to 'current' when user makes changes
  useEffect(() => {
    if (hasUserMadeChanges && activeScenarioName !== 'current') {
      setActiveScenarioName('current');
    }
  }, [hasUserMadeChanges, activeScenarioName]);

  // Calculate current bracket state
  const currentBracketState = {
    gamesPicked: winnersWithMetadata.filter(w => w && w.team && w.team.trim() !== '').length,
    totalGames: 63,
    hasUnsavedChanges: hasUserMadeChanges
  };

  // Get current player count
  const getCurrentPlayerCount = (): number => {
    if (!currentPlayerPicks) return 0;
    return getAllPlayerNames(currentPlayerPicks).length;
  };

  // Fetch pools from API
  const fetchPools = async () => {
    try {
      const userPools = await getUserPools();
      setPools(userPools);
      return userPools;
    } catch (error) {
      console.error('Error fetching pools:', error);
      setError('Failed to load pools');
      return [];
    }
  };

  // Fetch official winners from API
  const fetchOfficialWinners = async (): Promise<string[]> => {
    try {
      const winners = await getKnownWinners();
      setOfficialWinners(winners);
      return winners;
    } catch (error) {
      console.error('Error fetching official winners:', error);
      setOfficialWinners([]);
      return [];
    }
  };

  // Merge official winners with scenario data and validate bracket
  const mergeAndValidateWinners = (
    officials: string[],
    scenarioWinners: string[] = [],
    isLoadingScenario: boolean = false
  ): { mergedWinners: WinnerWithMetadata[], validationError?: string } => {
    const mergedWinners: WinnerWithMetadata[] = [];
    let validationError: string | undefined;
    let hasConflicts = false;

    // First pass: Add official winners and detect conflicts
    for (let i = 0; i < officials.length; i++) {
      const official = officials[i];
      if (official && typeof official === 'string' && official.trim() !== "" && official.trim() !== "xxx") {
        mergedWinners[i] = {
          team: official.trim(),
          source: WinnerSource.OFFICIAL
        };

        // Check for conflicts when loading a scenario
        if (isLoadingScenario && i < scenarioWinners.length) {
          const scenarioWinner = scenarioWinners[i];
          if (scenarioWinner &&
              typeof scenarioWinner === 'string' &&
              scenarioWinner.trim() !== "" &&
              scenarioWinner.trim() !== "xxx" &&
              scenarioWinner.trim() !== official.trim()) {
            hasConflicts = true;
          }
        }
      }
    }

    // Second pass: Add scenario winners where no official winner exists
    for (let i = 0; i < scenarioWinners.length; i++) {
      if (mergedWinners[i] && mergedWinners[i].source === WinnerSource.OFFICIAL) {
        continue;
      }

      const scenarioWinner = scenarioWinners[i];
      if (scenarioWinner && typeof scenarioWinner === 'string' && scenarioWinner.trim() !== "" && scenarioWinner.trim() !== "xxx") {
        mergedWinners[i] = {
          team: scenarioWinner.trim(),
          source: WinnerSource.USER
        };
      }
    }

    // Set conflict error message if conflicts detected
    if (hasConflicts) {
      validationError = "Some picks in this scenario conflict with official tournament results and have been updated";
    }

    // Validate bracket structure using existing validation logic
    const winnersArray = mergedWinners.map(w => w && w.team ? w.team : '');

    const { cleanedWinners, isValid, errorMessage } = validateAndCleanKnownWinners(winnersArray);

    if (!isValid) {
      if (validationError) {
        validationError = `${validationError}\n\n${errorMessage}`;
      } else {
        validationError = errorMessage;
      }

      const cleanedMerged: WinnerWithMetadata[] = [];
      for (let i = 0; i < cleanedWinners.length; i++) {
        if (cleanedWinners[i] && cleanedWinners[i].trim() !== '' && mergedWinners[i]) {
          cleanedMerged[i] = mergedWinners[i];
        }
      }

      return { mergedWinners: cleanedMerged, validationError };
    }

    return { mergedWinners, validationError };
  };

  // Update parent component with merged winners data
  const updateParentWithWinners = (officials: string[], scenarioWinners: string[] = [], isLoadingScenario: boolean = false) => {
    const { mergedWinners, validationError } = mergeAndValidateWinners(officials, scenarioWinners, isLoadingScenario);
    onWinnersUpdate(mergedWinners, validationError);
  };

  // Fetch scenarios
  const fetchScenarios = async () => {
    try {
      const userBrackets = await getUserBrackets();
      const mappedScenarios: SavedScenario[] = userBrackets.map((bracket: any) => ({
        name: bracket.name,
        knownWinners: bracket.knownWinners,
        created_at: bracket.created_at || new Date().toISOString()
      }));
      setSavedScenarios(mappedScenarios);
      return mappedScenarios;
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      setError('Failed to load scenarios');
      return [];
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const officials = await fetchOfficialWinners();
        updateParentWithWinners(officials, []);

        const [fetchedPools] = await Promise.all([
          fetchPools(),
          fetchScenarios()
        ]);

        // Auto-load pool: prefer last selected (from localStorage), fall back to most recent
        if (fetchedPools.length > 0 && !currentPlayerPicks.trim()) {
          const lastSelectedName = localStorage.getItem('lastSelectedPool');
          const preferredPool = (lastSelectedName && fetchedPools.find(p => p.name === lastSelectedName)) || fetchedPools[0];
          setCurrentPool(preferredPool);
          onPlayerPicksChange(preferredPool.playerPicks);

          // Apply pool configuration to context (only scoring)
          applyPoolConfiguration(preferredPool);

          // Extract player names
          const playerNames = getAllPlayerNames(preferredPool.playerPicks);
          onPlayersExtracted(playerNames);
        } else if (fetchedPools.length > 0) {
          // Find current pool based on player picks content
          const matchingPool = fetchedPools.find(p => p.playerPicks === currentPlayerPicks);
          if (matchingPool) {
            setCurrentPool(matchingPool);
            applyPoolConfiguration(matchingPool);
          }
        }

        // Notify parent of pool count (for onboarding wizard trigger)
        onPoolsLoaded?.(fetchedPools.map(p => p.name));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle external pool import (from onboarding wizard)
  useEffect(() => {
    if (externalImportPoolName) {
      handleAddPoolSuccess(externalImportPoolName);
    }
  }, [externalImportPoolName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pool management handlers
  const handleSwitchPool = async (poolName: string) => {
    const pool = pools.find(p => p.name === poolName);
    if (pool) {
      setCurrentPool(pool);
      onPlayerPicksChange(pool.playerPicks);

      // Apply pool configuration to context (only scoring)
      applyPoolConfiguration(pool);

      // Extract and update player names
      const playerNames = getAllPlayerNames(pool.playerPicks);
      onPlayersExtracted(playerNames);

      // Reset scenario to current when switching pools
      setActiveScenarioName('current');
    }
  };

  const handleEditPool = () => {
    setIsPoolEditModalOpen(true);
  };

  const handleCreatePool = () => {
    setIsCreatePoolModalOpen(true);
  };

  const handleAddPool = () => {
    setIsAddPoolModalOpen(true);
  };

  const handleAddPoolSuccess = async (poolName: string) => {
    setIsLoading(true);
    try {
      const updatedPools = await fetchPools();
      const importedPool = updatedPools.find(p => p.name === poolName);
      if (importedPool) {
        setCurrentPool(importedPool);
        onPlayerPicksChange(importedPool.playerPicks);
        applyPoolConfiguration(importedPool);
        const playerNames = getAllPlayerNames(importedPool.playerPicks);
        onPlayersExtracted(playerNames);
      }
      if (onFirstPoolImport) {
        onFirstPoolImport();
      }
    } catch (err) {
      console.error('Error loading imported pool:', err);
      setError('Pool imported but failed to load. Try switching to it manually.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEditedPool = async (newName: string, newData: string, newPointsConfig: {pointValues: number[]}) => {
    if (!currentPool) return;

    setIsLoading(true);
    setError('');

    try {
      // If name changed, delete old and create new
      if (currentPool.name !== newName) {
        await deletePool(currentPool.name);
      }

      // Save the pool with new data and point configuration
      await savePoolWithConfig(newName, newData, newPointsConfig);

      // Update current player picks and extract names
      onPlayerPicksChange(newData);
      const playerNames = getAllPlayerNames(newData);
      onPlayersExtracted(playerNames);

      // Create updated pool object
      const updatedPool: Pool = {
        name: newName,
        playerPicks: newData,
        created_at: new Date().toISOString(),
        pointsConfig: newPointsConfig
      };

      // Apply new configuration to context (only scoring)
      applyPoolConfiguration(updatedPool);

      // Refresh pools list and update current pool
      await fetchPools();
      setCurrentPool(updatedPool);

    } catch (error) {
      console.error('Error saving pool:', error);
      setError('Failed to save pool changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNewPool = async (newName: string, newData: string, newPointsConfig: {pointValues: number[]}) => {
    setIsLoading(true);
    setError('');

    try {
      await savePoolWithConfig(newName, newData, newPointsConfig);

      // Update current player picks and extract names
      onPlayerPicksChange(newData);
      const playerNames = getAllPlayerNames(newData);
      onPlayersExtracted(playerNames);

      // Create new pool object
      const newPool: Pool = {
        name: newName,
        playerPicks: newData,
        created_at: new Date().toISOString(),
        pointsConfig: newPointsConfig
      };

      // Apply new configuration to context (only scoring)
      applyPoolConfiguration(newPool);

      // Refresh pools and set as current
      const updatedPools = await fetchPools();
      const savedPool = updatedPools.find(p => p.name === newName);
      if (savedPool) {
        setCurrentPool(savedPool);
      }

      // Trigger help modal on first pool import
      if (onFirstPoolImport) {
        onFirstPoolImport();
      }

    } catch (error) {
      console.error('Error creating pool:', error);
      setError('Failed to create new pool');
    } finally {
      setIsLoading(false);
    }
  };

  // Scenario management handlers
  const handleSaveCurrentScenario = async (name: string) => {
    setIsLoading(true);
    setError('');

    try {
      // Convert WinnerWithMetadata to string array
      const knownWinners = winnersWithMetadata.map(winner => winner?.team || '');

      // Validate the bracket structure before saving
      const { cleanedWinners, isValid, errorMessage } = validateAndCleanKnownWinners(knownWinners);
      if (!isValid) {
        onValidationError(`Warning: Fixed inconsistencies in bracket structure: ${errorMessage}`);

        // Save with cleaned winners
        await saveBracket(name, cleanedWinners);

        // Clear validation error after successful save
        setTimeout(() => onValidationError(null), 5000);
      } else {
        // No issues, proceed normally
        await saveBracket(name, knownWinners);
      }

      // Refresh scenarios list
      await fetchScenarios();

      // Set this as the active scenario
      setActiveScenarioName(name);

    } catch (error) {
      console.error('Error saving scenario:', error);
      setError('Failed to save scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadScenario = async (scenarioLoadName: string) => {
    if (scenarioLoadName === 'current') {
      setActiveScenarioName('current');
      return;
    }

    const scenario = savedScenarios.find(s => s.name === scenarioLoadName);
    if (scenario) {
      setIsLoading(true);
      try {
        const officials = await fetchOfficialWinners();
        const scenarioWinners = scenario.knownWinners || [];

        const { cleanedWinners, isValid, errorMessage } = validateAndCleanKnownWinners(scenarioWinners);

        let scenarioWinnersToUse = scenarioWinners;
        if (!isValid) {
          onValidationError(`Warning: Fixed inconsistencies in loaded scenario: ${errorMessage}`);
          scenarioWinnersToUse = cleanedWinners;
          setTimeout(() => onValidationError(null), 5000);
        } else {
          onValidationError(null);
        }

        updateParentWithWinners(officials, scenarioWinnersToUse, true);
        setActiveScenarioName(scenarioLoadName);

      } catch (error) {
        console.error('Error loading scenario:', error);
        setError('Failed to load scenario');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteScenario = async (scenarioDeleteName: string) => {
    setIsLoading(true);
    setError('');

    try {
      await deleteScenario(scenarioDeleteName);
      await fetchScenarios();

      if (activeScenarioName === scenarioDeleteName) {
        setActiveScenarioName('current');
        updateParentWithWinners(officialWinners, []);
      }

    } catch (error) {
      console.error('Error deleting scenario:', error);
      setError('Failed to delete scenario');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHigherSeeds = () => {
    onFillWithHigherSeeds();
    setActiveScenarioName('current');
  };

  const handleLowerSeeds = () => {
    onFillWithLowerSeeds();
    setActiveScenarioName('current');
  };

  const handleClearBracket = () => {
    onClearBracket();
    setActiveScenarioName('current');
  };

  const handleClosePoolEditModal = () => {
    setIsPoolEditModalOpen(false);
  };

  const handleCloseCreatePoolModal = () => {
    setIsCreatePoolModalOpen(false);
  };

  const handleDeletePool = async (poolName: string) => {
    setIsLoading(true);
    setError('');

    try {
      // Call the delete API
      await deletePool(poolName);

      // Refresh pools list
      const updatedPools = await fetchPools();

      // If we deleted the current pool, switch to the first available pool or clear state
      if (currentPool && currentPool.name === poolName) {
        if (updatedPools.length > 0) {
          // Switch to the first available pool
          const newPool = updatedPools[0];
          setCurrentPool(newPool);
          onPlayerPicksChange(newPool.playerPicks);
          applyPoolConfiguration(newPool);

          const playerNames = getAllPlayerNames(newPool.playerPicks);
          onPlayersExtracted(playerNames);
        } else {
          // No pools left, clear state
          setCurrentPool(null);
          onPlayerPicksChange('');
          onPlayersExtracted([]);
        }
      }

    } catch (error) {
      console.error('Error deleting pool:', error);
      setError('Failed to delete pool');
    } finally {
      setIsLoading(false);
    }
  };

  // Watch for external save dialog trigger
  useEffect(() => {
    if (saveDialogTrigger && saveDialogTrigger > 0) {
      setShowSaveModal(true);
      setScenarioName('');
      setSaveError('');
    }
  }, [saveDialogTrigger]);

  // Watch for external share dialog trigger
  useEffect(() => {
    if (shareDialogTrigger && shareDialogTrigger > 0) {
      handleShareClick();
    }
  }, [shareDialogTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for external add pool (advanced) trigger
  useEffect(() => {
    if (advancedPoolTrigger && advancedPoolTrigger > 0) {
      setIsCreatePoolModalOpen(true);
    }
  }, [advancedPoolTrigger]);

  // Save scenario dialog handlers
  const handleSaveClick = () => {
    setShowSaveModal(true);
    setScenarioName('');
    setSaveError('');
  };

  const handleSaveConfirm = () => {
    if (!scenarioName.trim()) {
      setSaveError('Please enter a scenario name');
      return;
    }

    if (savedScenarios.some(s => s.name === scenarioName.trim())) {
      setSaveError('A scenario with this name already exists');
      return;
    }

    handleSaveCurrentScenario(scenarioName.trim());
    setShowSaveModal(false);
    setScenarioName('');
    setSaveError('');
  };

  const handleSaveCancel = () => {
    setShowSaveModal(false);
    setScenarioName('');
    setSaveError('');
  };

  // Share scenario handler
  const handleShareClick = async () => {
    if (!currentPool) return;

    setShowShareDialog(true);
    setShareUrl('');
    setShareCopied(false);
    setIsSharing(true);

    try {
      const knownWinnersArray = winnersWithMetadata.map(w => w?.team || '');
      const result = await createShare({
        scenarioName: activeScenarioName === 'current' ? 'Shared Scenario' : activeScenarioName,
        poolName: currentPool.name,
        knownWinners: knownWinnersArray,
        playerPicks: currentPlayerPicks,
        pointValues: currentPool.pointsConfig?.pointValues || [1, 2, 4, 8, 16, 32],
      });
      setShareUrl(result.shareUrl);
    } catch (err: any) {
      setError(err.message || 'Failed to create share link');
      setShowShareDialog(false);
    } finally {
      setIsSharing(false);
    }
  };

  const handleShareCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  // Delete pool dialog handlers
  const handleDeletePoolClick = (poolName: string) => {
    setPoolToDelete(poolName);
    setShowDeletePoolDialog(true);
  };

  const handleDeletePoolConfirm = () => {
    if (poolToDelete) {
      handleDeletePool(poolToDelete);
    }
    setShowDeletePoolDialog(false);
    setPoolToDelete(null);
  };

  const handleDeletePoolCancel = () => {
    setShowDeletePoolDialog(false);
    setPoolToDelete(null);
  };

  // Determine if Current Bracket is the active scenario
  const isCurrentActive = !activeScenarioName || activeScenarioName === 'current';

  const playerCount = getCurrentPlayerCount();

  // Pool selector UI (add button + dropdown, stacked full-width)
  const poolSelectorUI = (
    <div className="flex flex-col gap-2">
      <Button
        variant="outline"
        size="sm"
        className="w-full text-primary border-primary/30 hover:bg-primary/10"
        onClick={handleAddPool}
      >
        <Plus className="h-3.5 w-3.5 mr-1" />
        Add Pool
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="w-full gap-1.5 lg:max-w-[338px]">
            <FolderOpen className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{currentPool?.name || 'No Pool'}</span>
            {playerCount > 0 && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-0.5">
                {playerCount}
              </Badge>
            )}
            {currentPool && isCustomScoring(currentPool) && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 ml-0.5">
                Custom
              </Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[220px]">
          <DropdownMenuLabel>My Pools</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {pools.length > 0 ? (
            <DropdownMenuRadioGroup
              value={currentPool?.name || ''}
              onValueChange={(value) => handleSwitchPool(value)}
            >
              {pools.map((pool) => (
                <DropdownMenuRadioItem key={pool.name} value={pool.name} className="cursor-pointer">
                  <div className="flex items-center justify-between w-full gap-2">
                    <span className="truncate">{pool.name}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {getAllPlayerNames(pool.playerPicks || '').length}
                      </Badge>
                      {pool.pointsConfig && isCustomScoring(pool) && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          Custom
                        </Badge>
                      )}
                    </div>
                  </div>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">No pools yet</div>
          )}
          <DropdownMenuSeparator />
          {currentPool && (
            <>
              <DropdownMenuItem className="cursor-pointer" onSelect={handleEditPool}>
                <Pencil className="h-3.5 w-3.5 mr-2" />
                Edit Pool
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer text-destructive focus:text-destructive"
                onSelect={() => handleDeletePoolClick(currentPool.name)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete Pool
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem className="cursor-pointer" onSelect={handleCreatePool}>
            <Plus className="h-3.5 w-3.5 mr-2" />
            Create Manually...
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="relative">
      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded p-3 mb-3 text-destructive flex justify-between items-center text-sm">
          {error}
          <button
            className="bg-transparent border-none text-destructive cursor-pointer text-base font-bold p-0 w-5 h-5 flex items-center justify-center hover:opacity-70"
            onClick={() => setError('')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Pool selector: portal to external container if provided, otherwise render inline */}
      {poolSelectorPortal
        ? createPortal(poolSelectorUI, poolSelectorPortal)
        : poolSelectorUI
      }

      {/* Desktop scenario selector dropdown — portaled to bracket heading */}
      {scenarioSelectorPortal && createPortal(
        <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 w-full lg:w-[400px] justify-center">
              <span className="truncate">
                {isCurrentActive ? 'Current Bracket' : activeScenarioName}
              </span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {isCurrentActive
                  ? `${currentBracketState.gamesPicked}/${currentBracketState.totalGames}`
                  : (() => {
                      const s = savedScenarios.find(s => s.name === activeScenarioName);
                      if (!s) return '';
                      const count = s.knownWinners?.filter(w => w && w.trim() !== '' && w !== 'xxx').length || 0;
                      return `${count}/${currentBracketState.totalGames}`;
                    })()
                }
              </Badge>
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
            <DropdownMenuLabel>Load Scenario</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={activeScenarioName}
              onValueChange={(value) => handleLoadScenario(value)}
            >
              <DropdownMenuRadioItem value="current" className="cursor-pointer">
                <div className="flex items-center justify-between w-full">
                  <span>Current Bracket</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-2">
                    {currentBracketState.gamesPicked}/{currentBracketState.totalGames}
                  </Badge>
                </div>
              </DropdownMenuRadioItem>
              {savedScenarios.map((scenario) => {
                const gameCount = scenario.knownWinners
                  ? scenario.knownWinners.filter(w => w && w.trim() !== '' && w !== 'xxx').length
                  : 0;
                return (
                  <DropdownMenuRadioItem key={scenario.name} value={scenario.name} className="cursor-pointer group/item">
                    <div className="flex items-center justify-between w-full min-w-0">
                      <span className="truncate min-w-0 flex-1">{scenario.name}</span>
                      <div className="flex items-center gap-1 ml-2 shrink-0">
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {gameCount}/{currentBracketState.totalGames}
                        </Badge>
                        <button
                          className="h-4 w-4 rounded-full flex items-center justify-center lg:opacity-0 lg:group-hover/item:opacity-100 hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteScenario(scenario.name);
                          }}
                          title={`Delete ${scenario.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </DropdownMenuRadioItem>
                );
              })}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onSelect={handleHigherSeeds}>
              <ArrowUp className="h-3.5 w-3.5 mr-2" />
              Fill with Favorites
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" onSelect={handleLowerSeeds}>
              <ArrowDown className="h-3.5 w-3.5 mr-2" />
              Fill with Underdogs
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>,
        scenarioSelectorPortal
      )}

      {/* Mobile scenario toolbar (pills) — hidden on desktop, and hidden when portal is provided */}
      {!scenarioSelectorPortal && <div className="lg:hidden bg-card rounded-lg border px-4 py-2.5 flex items-center gap-2 flex-wrap mt-3">
        {/* Current Bracket pill */}
        <Button
          variant={isCurrentActive ? "default" : "outline"}
          size="sm"
          onClick={() => handleLoadScenario('current')}
          className="gap-1.5"
        >
          Current
          <Badge
            variant={isCurrentActive ? "outline" : "secondary"}
            className={cn(
              "text-[10px] px-1.5 py-0",
              isCurrentActive && "border-primary-foreground/30 text-primary-foreground"
            )}
          >
            {currentBracketState.gamesPicked}/{currentBracketState.totalGames}
          </Badge>
        </Button>

        {/* Higher Seeds pill */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleHigherSeeds}
          className="gap-1.5"
        >
          <ArrowUp className="h-3.5 w-3.5" />
          Higher Seeds
        </Button>

        {/* Lower Seeds pill */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleLowerSeeds}
          className="gap-1.5"
        >
          <ArrowDown className="h-3.5 w-3.5" />
          Lower Seeds
        </Button>

        {/* Saved scenario pills */}
        {savedScenarios.map((scenario) => {
          const isActive = activeScenarioName === scenario.name;
          const gameCount = scenario.knownWinners
            ? scenario.knownWinners.filter(w => w && w.trim() !== '' && w !== 'xxx').length
            : 0;

          return (
            <div key={scenario.name} className="group relative flex items-center">
              <Button
                variant={isActive ? "default" : "outline"}
                size="sm"
                onClick={() => handleLoadScenario(scenario.name)}
                className="gap-1.5 pr-7"
              >
                <span className="truncate max-w-[120px]">{scenario.name}</span>
                <Badge
                  variant={isActive ? "outline" : "secondary"}
                  className={cn(
                    "text-[10px] px-1.5 py-0",
                    isActive && "border-primary-foreground/30 text-primary-foreground"
                  )}
                >
                  {gameCount}
                </Badge>
              </Button>
              <button
                className="absolute right-1 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteScenario(scenario.name);
                }}
                title={`Delete ${scenario.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}

        {/* Share & Save buttons — mobile only, visible when scenario has changes */}
        {currentBracketState.hasUnsavedChanges && (
          <>
            <Button
              variant="default"
              size="sm"
              onClick={handleShareClick}
              disabled={!currentPool || currentBracketState.gamesPicked === 0}
              className="gap-1.5"
            >
              <Share2 className="h-3.5 w-3.5" />
              Share
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSaveClick}
              className="gap-1.5"
            >
              <Save className="h-3.5 w-3.5" />
              Save
            </Button>
          </>
        )}

        {/* Clear Bracket — mobile only */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearBracket}
          className="text-muted-foreground hover:text-destructive gap-1.5"
          title="Clear all user selections, keeping official winners"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Clear
        </Button>
      </div>}

      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2.5 bg-background/90 backdrop-blur-sm px-5 py-2.5 rounded-md shadow-md z-[100]">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm text-foreground">Processing...</span>
        </div>
      )}

      {/* Save Scenario Dialog */}
      <Dialog open={showSaveModal} onOpenChange={(open) => { if (!open) handleSaveCancel(); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Save Current Scenario</DialogTitle>
            <DialogDescription>
              Give your scenario a name to save the current bracket state.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label className="block mb-2 font-medium text-sm text-foreground">
              Scenario Name
            </label>
            <Input
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="Enter scenario name"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveConfirm();
                }
              }}
            />

            {saveError && (
              <div className="text-destructive text-xs mt-1 py-1">
                {saveError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleSaveCancel}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleSaveConfirm}>
              Save Scenario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Scenario Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Share Scenario</DialogTitle>
            <DialogDescription>
              Share a read-only snapshot of this scenario and pool standings.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isSharing ? (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Generating link...</span>
              </div>
            ) : shareUrl ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={shareUrl}
                    readOnly
                    className="text-sm font-mono"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareCopy}
                    className="shrink-0 gap-1.5"
                  >
                    {shareCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {shareCopied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Be careful! Anyone with this link can view the standings. This means the pool name and bracket names will be visible.
                </p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Pool Confirmation Dialog */}
      <Dialog open={showDeletePoolDialog} onOpenChange={(open) => { if (!open) handleDeletePoolCancel(); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Pool</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{poolToDelete}&rdquo;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeletePoolCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePoolConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pool Edit Modal */}
      {isPoolEditModalOpen && currentPool && (
        <PoolEditModal
          isOpen={isPoolEditModalOpen}
          onClose={handleClosePoolEditModal}
          poolName={currentPool.name}
          poolData={currentPool.playerPicks}
          pointsConfig={currentPool.pointsConfig}
          onSave={handleSaveEditedPool}
          isCreateMode={false}
          existingPoolNames={pools.map(p => p.name)}
        />
      )}

      {/* Create Pool Modal */}
      {isCreatePoolModalOpen && (
        <PoolEditModal
          isOpen={isCreatePoolModalOpen}
          onClose={handleCloseCreatePoolModal}
          poolName=""
          poolData=""
          pointsConfig={undefined}
          onSave={handleSaveNewPool}
          isCreateMode={true}
          existingPoolNames={pools.map(p => p.name)}
        />
      )}

      {/* Add Pool Import Modal */}
      {isAddPoolModalOpen && (
        <AddPoolModal
          isOpen={isAddPoolModalOpen}
          onClose={() => setIsAddPoolModalOpen(false)}
          onAdvanced={() => {
            setIsAddPoolModalOpen(false);
            setIsCreatePoolModalOpen(true);
          }}
          onSuccess={handleAddPoolSuccess}
        />
      )}
    </div>
  );
};

export default UnifiedPoolScenarioBar;
